import { useEffect, useState } from 'react';
import {
  QrCode,
  Inbox,
  Heart,
  ShoppingBag,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Loader2,
} from 'lucide-react';
import { adminApi } from '@/lib/bothyAuth';

/*
 * SanityCard — a wee "how the shop's doing this week" tile that lives at
 * the top of the Bothy dashboard. Compares the last 7 days to the 7 days
 * before that across four signals: QR scans, enquiries, wishlist events
 * and paid orders. Everything is read-only and fails silently — the
 * dashboard must never blank out because analytics are unavailable.
 */

const SIGNALS = [
  { key: 'qr_scans',        icon: QrCode,      label: 'Coming Home scans' },
  { key: 'enquiries',       icon: Inbox,       label: 'New enquiries' },
  { key: 'wishlist_events', icon: Heart,       label: 'Wishlist activity' },
  { key: 'orders',          icon: ShoppingBag, label: 'Paid orders' },
];

function trend(now, prev) {
  if (now === prev) return { dir: 'flat', pct: 0 };
  if (prev === 0) return { dir: 'up', pct: null };  // avoid ∞ %
  const pct = Math.round(((now - prev) / prev) * 100);
  return { dir: pct >= 0 ? 'up' : 'down', pct: Math.abs(pct) };
}

function Tile({ icon: Icon, label, now, prev }) {
  const t = trend(now, prev);
  const arrow =
    t.dir === 'up' ? ArrowUpRight :
    t.dir === 'down' ? ArrowDownRight :
    Minus;
  const arrowClass =
    t.dir === 'up'   ? 'text-[var(--brand-forest)]' :
    t.dir === 'down' ? 'text-[var(--brand-oak)]' :
                       'text-[var(--brand-muted)]';
  const Arrow = arrow;

  return (
    <div
      data-testid={`sanity-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="bg-[var(--brand-surface)] border border-[var(--brand-olive)]/25 border-l-4 border-l-[var(--brand-gold)] px-5 py-4"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)]">
        <Icon size={12} strokeWidth={2.5} />
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-3">
        <div className="font-display text-4xl leading-none text-[var(--brand-forest-deep)]">
          {now}
        </div>
        <div className={`inline-flex items-center gap-1 text-xs font-semibold ${arrowClass}`}>
          <Arrow size={13} strokeWidth={2.5} />
          {t.pct == null ? 'new' : `${t.pct}%`}
        </div>
      </div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[var(--brand-muted)]">
        Prev 7 days · {prev}
      </div>
    </div>
  );
}

export default function SanityCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .weeklyPulse()
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setErr(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section
        data-testid="sanity-card-loading"
        className="mb-8 grid place-items-center bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/20 py-10"
      >
        <Loader2 className="animate-spin text-[var(--brand-olive)]" size={22} />
      </section>
    );
  }

  if (err || !data) {
    // Fail-quiet: don't show anything if the endpoint isn't available.
    return null;
  }

  return (
    <section
      data-testid="sanity-card"
      className="mb-8 bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/25 p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--brand-forest-deep)]">
            Shop pulse · last 7 days
          </div>
          <h2 className="mt-1 font-display text-2xl text-[var(--brand-forest-deep)]">
            How the shop&rsquo;s doing this week.
          </h2>
        </div>
        <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--brand-olive)] font-semibold">
          vs. previous 7 days
        </span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SIGNALS.map((s) => {
          const Icon = s.icon;
          return (
            <Tile
              key={s.key}
              icon={Icon}
              label={s.label}
              now={data.this_week?.[s.key] ?? 0}
              prev={data.prev_week?.[s.key] ?? 0}
            />
          );
        })}
      </div>
    </section>
  );
}
